import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { BillingMethodChange } from './editor/rateCardSeed';
import { rateCardConsequence } from './editor/rateCardSeed';

/**
 * A contract that already carries budgets asks before its billing method
 * changes (decided 2026-09-15): the amounts stay, but the rate card does not
 * survive a move to fixed price (BR-11) and is created on a move to T&M — and
 * the user should read that before it happens, not discover it after.
 */
export type ChangeBillingMethodDialogProps = {
  change: BillingMethodChange | null;
  onConfirm: () => void;
  onCancel: () => void;
};

export const ChangeBillingMethodDialog = ({
  change,
  onConfirm,
  onCancel,
}: ChangeBillingMethodDialogProps) => {
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);
  const consequence = change ? rateCardConsequence(change, t) : null;
  return (
    <Dialog
      open={change !== null}
      onClose={onCancel}
      data-automation-id="billing-method-dialog"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {t('billingMethodDialog.Title', {
          method: change ? t(`pmCommon:billingMethod.${change.to}`) : '',
        })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="bodyRegular" color="text.secondary" sx={{ mb: consequence ? 1.5 : 0 }}>
          {t('billingMethodDialog.Body')}
        </Typography>
        {consequence ? (
          <Typography variant="bodyRegular" data-automation-id="billing-method-consequence">
            {consequence}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} data-automation-id="billing-method-cancel">
          {t('billingMethodDialog.Keep', {
            method: change ? t(`pmCommon:billingMethod.${change.from}`) : '',
          })}
        </Button>
        <Button variant="contained" onClick={onConfirm} data-automation-id="billing-method-confirm">
          {t('billingMethodDialog.Confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
